import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';
import CopyField from '~/components/Fields/CopyField';
import { showDoseStatisticsTableFullScreenState } from '~/state/atoms/viewerAtoms';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (typeof aValue === 'number' && typeof bValue === 'number') {
        return bValue - aValue;
    } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        return bValue.localeCompare(aValue);
    }
    return 0;
}

type Order = 'asc' | 'desc';

interface OptimizationObjective {
    id: number;
    structure: string;
    type?: string;
    dose: number;
    volumePrecent: number;
    priority: number;
    gEUD: number;
    expanded: boolean;
    subRows: Array<{
        type: string;
        dose: number;
        volumePrecent: number;
        priority: number;
        gEUD: number;
    }>;
}

function getComparator<Key extends keyof OptimizationObjective>(
    order: Order,
    orderBy: Key
): (a: OptimizationObjective, b: OptimizationObjective) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
    array: OptimizationObjective[],
    comparator: (a: OptimizationObjective, b: OptimizationObjective) => number
) {
    const stabilizedThis = array.map(
        (el, index) => [el, index] as [OptimizationObjective, number]
    );
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

interface OptimizationObjectivesTableProps {
    rows: OptimizationObjective[];
    onExpand: (index: number) => void;
    onCheckboxChange: (index: number) => void;
    checkedRows: boolean[];
}

interface OptimizationObjectivesTableHeadCell {
    disablePadding: boolean;
    id: keyof OptimizationObjective;
    label: string;
    numeric: boolean;
}

const headCells: readonly OptimizationObjectivesTableHeadCell[] = [
    { id: 'id', numeric: false, disablePadding: true, label: '' },
    {
        id: 'structure',
        numeric: false,
        disablePadding: false,
        label: 'Structure',
    },
    { id: 'type', numeric: false, disablePadding: false, label: 'Type' },
    {
        id: 'volumePrecent',
        numeric: true,
        disablePadding: false,
        label: 'Volume (%)',
    },
    { id: 'gEUD', numeric: true, disablePadding: false, label: 'gEUD a' },
    { id: 'dose', numeric: true, disablePadding: false, label: 'Dose (Gy)' },
    {
        id: 'priority',
        numeric: true,
        disablePadding: false,
        label: 'Priority',
    },
];

interface OptimizationGoalsTableHeadProps {
    onRequestSort: (
        event: React.MouseEvent<unknown>,
        property: keyof OptimizationObjective
    ) => void;
    order: Order;
    orderBy: string;
}

const OptimizationGoalsTableHead = ({
    order,
    orderBy,
    onRequestSort,
}: OptimizationGoalsTableHeadProps) => {
    const createSortHandler =
        (property: keyof OptimizationObjective) =>
        (event: React.MouseEvent<unknown>) =>
            onRequestSort(event, property);
    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell key={headCell.id} align="left">
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                            sx={(theme) => ({
                                '& .MuiTableSortLabel-icon': {
                                    color:
                                        orderBy === headCell.id
                                            ? `${theme.palette.primary.main}!important`
                                            : theme.palette.text.primary,
                                },
                            })}
                        >
                            {headCell.label}
                        </TableSortLabel>
                    </TableCell>
                ))}
                <TableCell key={'expand'} align="left" />
            </TableRow>
        </TableHead>
    );
};

const OptimizationObjectivesTable: React.FC<
    OptimizationObjectivesTableProps
> = ({ rows, onExpand }) => {
    const [showDoseStatisticsTableFullScreen] = useRecoilState(
        showDoseStatisticsTableFullScreenState
    );
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] =
        useState<keyof OptimizationObjective>('structure');
    const handleRequestSort = (
        _event: React.MouseEvent<unknown>,
        property: keyof OptimizationObjective
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const rowsToDisplay = useMemo(
        () => stableSort(rows, getComparator(order, orderBy)),
        [order, orderBy, rows]
    );

    return (
        <Paper
            sx={{
                width: '100%',
                position: 'absolute',
                height: showDoseStatisticsTableFullScreen ? '100%' : 'auto',
            }}
        >
            <TableContainer>
                <Table size="small">
                    <OptimizationGoalsTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody sx={{ height: '100%' }}>
                        {rowsToDisplay.map((row, index) => (
                            <React.Fragment key={row.id}>
                                <TableRow key={row.id}>
                                    <TableCell>
                                        <IconButton
                                            onClick={() => onExpand(row.id)}
                                        >
                                            {row.expanded ? (
                                                <ExpandLess />
                                            ) : (
                                                <ExpandMore />
                                            )}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>{row.structure}</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>
                                        <CopyField
                                            label={row.volumePrecent.toFixed(1)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <CopyField
                                            label={row.gEUD.toFixed(1)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <CopyField
                                            label={row.dose.toFixed(1)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <CopyField label={row.priority} />
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                                {row.expanded &&
                                    row.subRows.map((subRow, subIndex) => (
                                        <TableRow
                                            key={`${index}-${subIndex}`}
                                            sx={{ height: '60px' }}
                                        >
                                            <TableCell />
                                            <TableCell />
                                            <TableCell>
                                                <CopyField
                                                    label={subRow.type}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <CopyField
                                                    label={subRow.volumePrecent.toFixed(
                                                        1
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <CopyField
                                                    label={subRow.gEUD.toFixed(
                                                        1
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <CopyField
                                                    label={subRow.dose.toFixed(
                                                        1
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <CopyField
                                                    label={subRow.priority}
                                                />
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    ))}
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default OptimizationObjectivesTable;
